import axios, { AxiosError, type AxiosRequestConfig, type Method } from 'axios';

import type {
	IBaseErrorResponse,
	ObjectType,
} from '@incloodsolutions/toolkit/interface';

import type { IBaseApiResponse } from '@workinance/shared/interfaces/base.interface';

import { apiUrl } from '@/config';
import { useAuthStore } from '@/store/auth.store';

// The API used to nest its payload under `data` ({ success, message, data: { store } }). Newer versions of the API
// toolkit spread it at the top level ({ success, statusCode, store }). Accept both, so the stores can keep reading `data`.
function normalizeApiResponse(body: any) {
	if (!body || typeof body !== 'object' || 'data' in body) {
		return body;
	}
	const { success, statusCode, message, timestamp, ...payload } = body;
	return { success, statusCode, message, timestamp, data: payload };
}

// Failed responses may carry `{ error: { message, ... } }` or only `{ success: false, statusCode }`.
function getResponseErrorObject(error: AxiosError<any>) {
	const responseBody = error.response?.data;
	return (
		responseBody?.error ?? {
			statusCode: responseBody?.statusCode ?? error.response?.status,
			message:
				responseBody?.message ||
				error.response?.statusText ||
				error.message ||
				'Request failed',
			path: error.config?.url,
			method: error.config?.method as Method,
			timestamp: responseBody?.timestamp,
		}
	);
}

type RetryableRequestConfig = AxiosRequestConfig & { _hasRetried?: boolean };

// Several requests can hit an expired token at once; they all wait for one refresh instead of each starting their own.
let pendingRefresh: Promise<string> | null = null;

function refreshAccessToken(): Promise<string> {
	pendingRefresh ??= (async () => {
		try {
			const { refreshToken, setAccessToken } = useAuthStore.getState();
			const { data: body } = await axios.post(`${apiUrl}/auth/refresh`, {
				refreshToken,
			});
			const token: string | undefined = body?.data?.token ?? body?.token;
			if (!token) throw new Error('No token in the refresh response');
			await setAccessToken(token);
			return token;
		} catch (error: any) {
			throw Object.assign(error ?? {}, { refreshFailed: true });
		}
	})().finally(() => {
		pendingRefresh = null;
	});
	return pendingRefresh;
}

export async function sendHttpRequest<
	TResponse = any,
	TBody extends ObjectType = any,
>(
	options: Required<Pick<AxiosRequestConfig<TBody>, 'url'>> &
		Omit<AxiosRequestConfig<TBody>, 'url'> & { ignoreInterceptor?: boolean },
) {
	try {
		options.ignoreInterceptor = options?.ignoreInterceptor || false;
		if (!options.ignoreInterceptor) {
			return getAxiosInstance()(
				options,
			) as unknown as IBaseApiResponse<TResponse>;
		}
		const response = await axios(options);
		return normalizeApiResponse(
			response.data,
		) as unknown as IBaseApiResponse<TResponse>;
	} catch (error) {
		console.error('sendHttpRequest error ->', error);
		const errorObject = !options.ignoreInterceptor
			? error
			: error?.response?.data;
		const message =
			errorObject?.message ||
			errorObject ||
			error?.message ||
			'Http call errored out!';
		throw { ...errorObject, message };
	}
}

function getAxiosInstance() {
	const instance = axios.create({ baseURL: apiUrl });

	instance.interceptors.response.use(
		({ data }) => normalizeApiResponse(data),
		async (error: AxiosError<any>) => {
			// An expired access token: renew it with the refresh token and run the request again, once.
			// The user never notices. If the refresh token is no longer good, they are signed out.
			const originalConfig = error.config as RetryableRequestConfig | undefined;
			// Only the calls that don't need (or can't use) an existing token: login/register have none
			// yet, forgot/reset-password are anonymous, and refresh/logout manage the token itself. This
			// must NOT also exclude auth-guarded routes like verification/verification-resend -- those
			// need the same refresh-and-retry as everything else, or a session whose access token expires
			// mid-verification gets permanently stuck 401-ing with no way to recover.
			const NON_RETRYABLE_AUTH_PATHS = [
				'auth/login',
				'auth/register',
				'auth/logout',
				'auth/refresh',
				'auth/forgot-password',
				'auth/reset-password',
			];
			const isAuthCall = NON_RETRYABLE_AUTH_PATHS.includes(
				originalConfig?.url ?? '',
			);
			if (
				error.response?.status === 401 &&
				originalConfig &&
				!originalConfig._hasRetried &&
				!isAuthCall &&
				useAuthStore.getState().refreshToken
			) {
				try {
					await refreshAccessToken();
					originalConfig._hasRetried = true;
					return await instance.request(originalConfig);
				} catch (refreshError: any) {
					// A failed retry (not a failed refresh) is an ordinary error; a failed refresh ends the session.
					if (refreshError?.refreshFailed) useAuthStore.getState().logout();
					else throw refreshError;
				}
			}

			let errorObject: IBaseErrorResponse;

			if (
				error.response &&
				error.response.status > 399 &&
				error.response.status < 600
			) {
				errorObject = getResponseErrorObject(error);
			} else {
				const newError = ((error as any).error as AxiosError) || error;
				const errorResponse = newError?.response as any;

				errorObject = {
					statusCode: newError?.response?.status || 500,
					message:
						newError?.response?.statusText ||
						errorResponse?.message ||
						error.message,
					path: errorResponse?.path,
					method: errorResponse?.method as Method,
					timestamp: errorResponse?.timestamp,
				};
			}
			return Promise.reject({ ...errorObject });
		},
	);

	instance.interceptors.request.use(
		async (config) => {
			const { token, activeBusinessId } = useAuthStore.getState();

			config.headers = config.headers ?? {};
			if (typeof window !== 'undefined' && !window.navigator.onLine) {
				return Promise.reject({
					error: {
						response: {
							message: 'No internet connection!',
							path: config.url,
							method: config.method,
							timestamp: new Date().toISOString(),
						},
					},
				});
			}
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
			}
			if (activeBusinessId) {
				config.headers.business_id = activeBusinessId;
			}
			return config;
		},
		(error) => Promise.reject({ error }),
	);

	return instance;
}
